import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Input,
  Text,
  VStack,
  Flex,
  Button,
} from "@chakra-ui/react";

/**
 * SearchableSelect Component
 * A reusable searchable dropdown with keyboard navigation
 *
 * @param {Object} props
 * @param {Array} props.options - Array of options to display
 * @param {string} props.getOptionLabel - Function to get the label from an option (default: option => option.name)
 * @param {string} props.getOptionValue - Function to get the value from an option (default: option => option.id)
 * @param {Array} props.selectedValues - Array of selected values (for multi-select)
 * @param {Function} props.onChange - Called when selection changes (value for single, array for multi)
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.multiSelect - Enable multi-select mode
 * @param {string} props.size - Input size (sm, md, lg)
 * @param {boolean} props.isDisabled - Disable the input
 */
export default function SearchableSelect({
  options = [],
  getOptionLabel = (option) => option.name,
  getOptionValue = (option) => option.id,
  selectedValues = [],
  onChange,
  placeholder = "Search...",
  multiSelect = false,
  size = "sm",
  isDisabled = false,
}) {
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);

  // Filter options based on search
  const filteredOptions = options.filter((option) =>
    getOptionLabel(option).toLowerCase().includes(search.toLowerCase())
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setShowDropdown(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowDropdown(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  // Handle selection
  const handleSelect = (option) => {
    const value = getOptionValue(option);

    if (multiSelect) {
      const newValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(newValues);
    } else {
      onChange(value);
      setShowDropdown(false);
      setSearch("");
    }
  };

  // Get display value for input
  const getDisplayValue = () => {
    if (showDropdown) return search;
    if (!multiSelect && selectedValues.length === 1) {
      const selected = options.find((opt) => getOptionValue(opt) === selectedValues[0]);
      return selected ? getOptionLabel(selected) : "";
    }
    return "";
  };

  return (
    <Box position="relative" ref={containerRef}>
      <Input
        type="search"
        name="searchable-select"
        placeholder={placeholder}
        value={getDisplayValue()}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => {
          if (!isDisabled) {
            setShowDropdown(true);
            setSearch("");
          }
        }}
        onKeyDown={handleKeyDown}
        size={size}
        isDisabled={isDisabled}
        isReadOnly={isDisabled}
        bg={isDisabled ? "gray.50" : "white"}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck={false}
      />

      {showDropdown && !isDisabled && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
          maxH="200px"
          overflowY="auto"
          zIndex={10}
        >
          {filteredOptions.length === 0 ? (
            <Box p={3} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                {options.length > 0 ? "No matches found" : "No options available"}
              </Text>
            </Box>
          ) : (
            filteredOptions.map((option, index) => {
              const value = getOptionValue(option);
              const isSelected = selectedValues.includes(value);
              const isHighlighted = index === highlightedIndex;

              return (
                <Flex
                  key={value}
                  p={2}
                  align="center"
                  cursor="pointer"
                  bg={isHighlighted ? "blue.50" : isSelected ? "gray.50" : "white"}
                  _hover={{ bg: isHighlighted ? "blue.100" : "gray.50" }}
                  onClick={() => handleSelect(option)}
                >
                  {multiSelect && (
                    <Box
                      w="16px"
                      h="16px"
                      mr={2}
                      border="2px solid"
                      borderColor={isSelected ? "blue.500" : "gray.300"}
                      borderRadius="sm"
                      bg={isSelected ? "blue.500" : "white"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {isSelected && (
                        <Text color="white" fontSize="10px" fontWeight="bold">
                          ✓
                        </Text>
                      )}
                    </Box>
                  )}
                  <Text fontSize="sm" fontWeight={isSelected ? "medium" : "normal"}>
                    {getOptionLabel(option)}
                  </Text>
                </Flex>
              );
            })
          )}
        </Box>
      )}
    </Box>
  );
}
